import { useState, useEffect, useMemo, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { supabase, LogEntry, Car, Expense } from '../lib/supabase';
import { useTranslations } from '../i18n';
import { usePreferences } from '../contexts/PreferencesContext';
import { normalizeExpenseTypeKey, getExpenseTypeNameFromData, SupportedLanguage } from '../lib/expenseTypeHelpers';
import { formatCurrency } from '../lib/conversions';
import { LogEntryItem } from '../components/cars/LogEntryItem';
import { SparePartItem } from '../components/cars/SparePartItem';
import { NavigationBar } from '../components/ui/NavigationBar';

import { DynamicIcon } from '../lib/icons';
import { theme } from '../lib/theme';
import { generateExpensePDF, ExportExpense } from '../lib/pdfExport';
import defaultExpenseTypesData from '../data/defaultExpenseTypes.json';
import { FileDown, Image as ImageIcon, Wrench, Loader2 } from 'lucide-react';
import { GalleryItem } from '../components/cars/GalleryItem';
import { isPdfFile } from '../lib/pdfUtils';
import { ImageViewer } from '../components/ui/ImageViewer';
import { PdfViewer } from '../components/ui/PdfViewer';
import { DateInput } from '../components/ui/DateInput';
import { AlertModal } from '../components/ui/AlertModal';


// ─── iOS-style toggle ──────────────────────────────────────────────────────────
function Toggle({ checked, onChange, id }: { checked: boolean; onChange: (v: boolean) => void; id?: string }) {
    return (
        <button
            id={id}
            type="button"
            role="switch"
            aria-checked={checked}
            onClick={() => onChange(!checked)}
            className={`relative inline-flex items-center flex-shrink-0 w-12 h-7 rounded-full transition-colors duration-200 focus:outline-none ${checked ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'}`}
        >
            <span
                className={`inline-block w-5 h-5 rounded-full bg-white shadow-md transform transition-transform duration-200 ${checked ? 'translate-x-6' : 'translate-x-1'}`}
            />
        </button>
    );
}

type ExpenseImage = {
    id: string;
    image_url: string;
    log_entry_id: string;
    comment?: string;
    image_url_thumb?: string;
};

type ExpenseSparePart = {
    log_entry_id: string;
    spare_part_id: string;
    quantity: number;
    price_per_unit: number;
    spare_parts: {
        id: string;
        name: string;
        part_code?: string;
        comment?: string;
        created_at: string;
    };
};

type PerExpenseAttachment = {
    id: string;
    logEntryId: string;
    expenseTitle: string;
    fileName: string;
    url: string;
    type: 'image' | 'pdf';
    thumbnailUrl?: string;
    comment?: string;
};

type PerExpensePart = {
    joinId: string;
    expenseId: string;
    part: {
        id: string;
        name: string;
        part_code?: string;
        price?: number;
        quantity?: number;
        comment?: string;
        created_at: string;
    };
};

type ExportViewProps = {
    onClose: () => void;
    // Allow passing from props if context is unavailable
    logEntries?: LogEntry[];
    selectedCar?: Car;
};

export default function ExportView({ onClose, logEntries: propsLogEntries, selectedCar: propsCar }: ExportViewProps) {
    const context = useOutletContext<any>() || {};
    const selectedCar = propsCar || context.selectedCar;
    const { isOwner, isCarAdmin, isAdmin, isAdminView, ownerPreferences } = context;
    const currentPlan = ownerPreferences?.plan_type || 'free';
    const isRestricted = false; // currentPlan === 'free' && !isAdminView;

    const logEntries = propsLogEntries || context.logEntries || [];
    const { t, language } = useTranslations();
    const { preferences: userPreferences } = usePreferences();

    const isAuthorized = isOwner || isCarAdmin || isAdmin || isAdminView;

    const effectivePreferences = selectedCar ? {
        ...(userPreferences || context.preferences),
        distance_unit: selectedCar.distance_unit || (userPreferences || context.preferences)?.distance_unit,
        volume_unit: selectedCar.volume_unit || (userPreferences || context.preferences)?.volume_unit,
        currency: selectedCar.currency || (userPreferences || context.preferences)?.currency,
        currency_symbol: selectedCar.currency_symbol || (userPreferences || context.preferences)?.currency_symbol
    } : null;

    const [dateFrom, setDateFrom] = useState<string>('');
    const [dateTo, setDateTo] = useState<string>('');
    const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
    const [showFormatSelector, setShowFormatSelector] = useState(false);
    const navigate = useNavigate();

    const allTypeKeys = useMemo(() => {
        const keys = new Set<string>();
        logEntries.forEach((entry: LogEntry) => {
            (entry.expenses || []).forEach(exp => {
                keys.add(normalizeExpenseTypeKey(exp.type));
            });
        });
        return Array.from(keys);
    }, [logEntries]);

    const [activeTypes, setActiveTypes] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (allTypeKeys.length > 0 && activeTypes.size === 0) {
            setActiveTypes(new Set(allTypeKeys));
        }
    }, [allTypeKeys]);

    const [expenseImages, setExpenseImages] = useState<ExpenseImage[]>([]);
    const [expenseParts, setExpenseParts] = useState<ExpenseSparePart[]>([]);

    // per-expense enabled toggle
    const [enabledExpenses, setEnabledExpenses] = useState<Set<string>>(new Set());
    // per-image attachment toggle
    const [enabledImages, setEnabledImages] = useState<Set<string>>(new Set());
    // per-spare-part attachment toggle
    const [enabledParts, setEnabledParts] = useState<Set<string>>(new Set());

    const [globalIncludeFiles, setGlobalIncludeFiles] = useState(true);
    const [globalIncludeParts, setGlobalIncludeParts] = useState(true);

    const [generating, setGenerating] = useState(false);

    // ── Viewer state ───────────────────────────────────────────────────────────
    const [viewerImages, setViewerImages] = useState<string[] | null>(null);
    const [viewerIndex, setViewerIndex] = useState(0);
    const [pdfViewerUrl, setPdfViewerUrl] = useState<string | null>(null);

    // ── Loaders ────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!selectedCar?.id || logEntries.length === 0) return;
        const ids = logEntries.map((e: LogEntry) => e.id);

        supabase
            .from('log_entry_images')
            .select('id, image_url, log_entry_id, comment, image_url_thumb')
            .in('log_entry_id', ids)
            .then(({ data }) => setExpenseImages((data || []) as unknown as ExpenseImage[]));

        supabase
            .from('log_entry_parts')
            .select('log_entry_id, spare_part_id, quantity, price_per_unit, spare_parts(id, name, part_code, comment, created_at)')
            .in('log_entry_id', ids)
            .then(({ data }) => setExpenseParts((data || []) as unknown as ExpenseSparePart[]));
    }, [selectedCar?.id, logEntries.length]);

    // ── Helpers ────────────────────────────────────────────────────────────────
    const getTypeInfo = useCallback((type: string) => {
        const key = normalizeExpenseTypeKey(type);
        return (defaultExpenseTypesData as any)[key] || { icon: 'Circle', color: '#6B7280' };
    }, []);

    const getTypeName = useCallback((type: string) => {
        const key = normalizeExpenseTypeKey(type);
        const info = (defaultExpenseTypesData as any)[key];
        if (info) return getExpenseTypeNameFromData(info, language as SupportedLanguage);
        return (t.expenses.typeLabels as any)[type] || type;
    }, [language, t.expenses.typeLabels]);

    // ── Filtering ──────────────────────────────────────────────────────────────
    const visibleEntries = useMemo(() =>
        logEntries
            .filter((e: LogEntry) => {
                const types = (e.expenses || []).map(exp => normalizeExpenseTypeKey(exp.type));
                const matchesType = types.length === 0 || types.some(t => activeTypes.has(t));
                const inDateRange = (!dateFrom || e.date >= dateFrom) && (!dateTo || e.date <= dateTo);
                return matchesType && inDateRange;
            })
            .sort((a: LogEntry, b: LogEntry) => b.date.localeCompare(a.date)),
        [logEntries, activeTypes, dateFrom, dateTo]
    );

    useEffect(() => {
        setEnabledExpenses(new Set(visibleEntries.map((e: LogEntry) => e.id)));
    }, [visibleEntries.length, dateFrom, dateTo, activeTypes.size]);

    // ── Index by expense ───────────────────────────────────────────────────────
    const imagesByExpense = useMemo(() => {
        const map: Record<string, ExpenseImage[]> = {};
        expenseImages.forEach(img => {
            if (!map[img.log_entry_id]) map[img.log_entry_id] = [];
            map[img.log_entry_id].push(img);
        });
        return map;
    }, [expenseImages]);

    const partsByExpense = useMemo(() => {
        const map: Record<string, PerExpensePart[]> = {};
        expenseParts.forEach(row => {
            const basePart = Array.isArray((row as any).spare_parts) ? (row as any).spare_parts[0] : row.spare_parts;
            if (!basePart) return;
            const part = { ...basePart, quantity: row.quantity, price: row.price_per_unit };
            if (!map[row.log_entry_id]) map[row.log_entry_id] = [];
            const joinId = `${row.log_entry_id}_${row.spare_part_id}`;
            map[row.log_entry_id].push({ joinId, expenseId: row.log_entry_id, part });
        });
        return map;
    }, [expenseParts]);

    const allAttachments = useMemo((): PerExpenseAttachment[] => {
        const atts: PerExpenseAttachment[] = [];
        visibleEntries.forEach((e: LogEntry) => {
            (imagesByExpense[e.id] || []).forEach(img => {
                const isPdf = isPdfFile(img.image_url);
                atts.push({
                    id: img.id,
                    logEntryId: e.id,
                    expenseTitle: e.title || (e.expenses && e.expenses.length > 0 ? Array.from(new Set(e.expenses.map(exp => getTypeName(exp.type)))).join(', ') : t.expenses.title),
                    fileName: img.image_url.split('/').pop() || 'file',
                    url: img.image_url,
                    type: isPdf ? 'pdf' : 'image',
                    thumbnailUrl: img.image_url_thumb,
                    comment: img.comment,
                });
            });
        });
        return atts;
    }, [visibleEntries, imagesByExpense, getTypeName, t.expenses.title]);

    useEffect(() => {
        setEnabledImages(prev => {
            const next = new Set(prev);
            allAttachments.forEach(a => {
                if (!next.has(a.id) && enabledExpenses.has(a.logEntryId)) {
                    if (globalIncludeFiles) next.add(a.id);
                }
            });
            return next;
        });
    }, [allAttachments.length, globalIncludeFiles, enabledExpenses]);

    useEffect(() => {
        setEnabledParts(prev => {
            const next = new Set(prev);
            Object.values(partsByExpense).flat().forEach(p => {
                if (!next.has(p.joinId) && enabledExpenses.has(p.expenseId)) {
                    if (globalIncludeParts) next.add(p.joinId);
                }
            });
            return next;
        });
    }, [expenseParts.length, globalIncludeParts, enabledExpenses]);

    const handleGlobalFilesToggle = (v: boolean) => {
        setGlobalIncludeFiles(v);
        setEnabledImages(prev => {
            const next = new Set(prev);
            allAttachments.forEach(a => {
                if (enabledExpenses.has(a.logEntryId)) {
                    if (v) next.add(a.id);
                    else next.delete(a.id);
                }
            });
            return next;
        });
    };

    const handleGlobalPartsToggle = (v: boolean) => {
        setGlobalIncludeParts(v);
        setEnabledParts(prev => {
            const next = new Set(prev);
            Object.values(partsByExpense).flat().forEach(p => {
                if (enabledExpenses.has(p.expenseId)) {
                    if (v) next.add(p.joinId);
                    else next.delete(p.joinId);
                }
            });
            return next;
        });
    };

    const grouped = useMemo(() => {
        const years: Record<string, { year: string, months: { key: string, label: string, expenses: LogEntry[] }[] }> = {};
        visibleEntries.forEach((e: LogEntry) => {
            const d = new Date(e.date);
            const year = d.getFullYear().toString();
            const monthLabel = d.toLocaleDateString(language, { month: 'long' });
            const monthKey = `${year}-${d.getMonth()}`;

            if (!years[year]) years[year] = { year, months: [] };
            let mObj = years[year].months.find(m => m.key === monthKey);
            if (!mObj) {
                mObj = { key: monthKey, label: monthLabel, expenses: [] };
                years[year].months.push(mObj);
            }
            mObj.expenses.push(e);
        });
        return Object.values(years).sort((a, b) => b.year.localeCompare(a.year));
    }, [visibleEntries, language]);

    const hasAnyFiles = expenseImages.length > 0;
    const hasAnyParts = expenseParts.length > 0;

    const selectedTotal = useMemo(() => {
        return visibleEntries
            .filter((e: LogEntry) => enabledExpenses.has(e.id))
            .reduce((s: number, e: LogEntry) => {
                const entryTotal = (e.expenses || [])
                    .filter(exp => exp.type !== 'info')
                    .reduce((sum, exp) => sum + (exp.amount || 0), 0);
                return s + entryTotal;
            }, 0);
    }, [visibleEntries, enabledExpenses]);

    const fmt = (val: number) => effectivePreferences ? formatCurrency(val, effectivePreferences.currency, effectivePreferences.currency_symbol) : `€${val.toFixed(2)}`;

    const handleGeneratePDF = async () => {
        if (generating) return;
        if (isRestricted) {
            setIsUpgradeModalOpen(true);
            return;
        }
        setGenerating(true);
        try {
            const selectedExps = visibleEntries.filter((e: LogEntry) => enabledExpenses.has(e.id));
            const expensesForPdf: ExportExpense[] = selectedExps.map((e: LogEntry) => {
                const expenses = e.expenses || [];
                const totalAmount = expenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
                const principalExpense = expenses.length > 0 ? expenses[0] : null;

                const normKey = principalExpense ? normalizeExpenseTypeKey(principalExpense.type) : 'other';
                const typeData = (defaultExpenseTypesData as any)[normKey];

                let displayTitle = e.title;
                if (!displayTitle) {
                    if (expenses.length > 0) {
                        displayTitle = expenses.map((exp: Expense) => getTypeName(exp.type)).join(', ');
                    } else {
                        displayTitle = t.expenses.editExpense;
                    }
                }

                return {
                    id: e.id,
                    date: e.date,
                    type: principalExpense?.type || 'other',
                    typeName: principalExpense ? getTypeName(principalExpense.type) : 'Other',
                    typeColor: typeData?.color || '#3B82F6',
                    typeIcon: typeData?.icon || 'circle',
                    title: displayTitle,
                    description: expenses.map(exp => exp.description).filter(Boolean).join('\n'),
                    amount: totalAmount,
                    mileage: e.mileage || undefined,
                    fuelLiters: expenses.reduce((sum, exp) => sum + (exp.fuel_liters || 0), 0) || undefined,
                    electricityKwh: expenses.reduce((sum, exp) => sum + (exp.electricity_kwh || 0), 0) || undefined,
                    parts: (partsByExpense[e.id] || [])
                        .filter(p => enabledParts.has(p.joinId))
                        .map(p => ({
                            name: p.part.name,
                            partCode: p.part.part_code,
                            quantity: p.part.quantity,
                            price: p.part.price
                        }))
                };
            });

            const selectedAtts = allAttachments
                .filter(a => enabledImages.has(a.id))
                .map(a => ({
                    id: a.id,
                    expenseId: a.logEntryId,
                    expenseTitle: a.expenseTitle,
                    fileName: a.fileName,
                    url: a.url,
                    type: a.type
                }));
            const firstDate = new Date(selectedExps[selectedExps.length - 1]?.date || '');
            const lastDate = new Date(selectedExps[0]?.date || '');
            const selectedMonths = Math.max(1, (lastDate.getFullYear() - firstDate.getFullYear()) * 12 + (lastDate.getMonth() - firstDate.getMonth()) + 1);

            const formatDateShort = (d: Date) => {
                if (isNaN(d.getTime())) return '...';
                return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
            };

            const bestCarImageUrl = selectedCar?.image_url || expenseImages[0]?.image_url || null;

            await generateExpensePDF({
                carName: selectedCar?.name || 'Car',
                carMake: selectedCar?.make,
                carModel: selectedCar?.model,
                carYear: selectedCar?.year,
                carPlate: selectedCar?.plate_number,
                carImageUrl: bestCarImageUrl,
                carVin: selectedCar?.vin_number,
                carFuelType: selectedCar?.fuel_type,
                expenses: expensesForPdf,
                attachments: selectedAtts,
                currency: effectivePreferences?.currency || 'EUR',
                currencySymbol: effectivePreferences?.currency_symbol || '€',
                language,
                dateFrom: dateFrom || undefined,
                dateTo: dateTo || undefined,
                fullPeriod: `${formatDateShort(firstDate)} – ${formatDateShort(lastDate)}`,
                totalAmount: selectedTotal,
                avgMonthly: selectedTotal / selectedMonths,
                cpk: 0,
                distanceUnit: effectivePreferences?.distance_unit === 'miles' ? 'mi' : 'km',
                labels: {
                    date: t.expenses.date,
                    type: t.expenses.type,
                    title: t.expenses.expenseTitle,
                    amount: t.expenses.amount,
                    summary: (t.export as any).summary || 'Summary',
                    totalCost: (t.export as any).totalExpenses || 'Total',
                    avgMonth: (t.expenses?.stats?.avgMonth) || 'Avg / Month',
                    byType: (t.statistics?.byType) || 'By Type',
                    count: (t.export as any).countLabel || 'Count',
                    ofTotal: (t.export as any).ofTotalLabel || '% of Total',
                    generated: (t.export as any).generatedOn || 'Generated',
                    period: (t.export as any).period || 'Period',
                    expensesSection: (t.export as any).expensesSection || 'Expenses',
                    liter: (t.common?.units?.liters) || 'l',
                    gallon: (t.common?.units?.gallons) || 'gal',
                    avgCost: (t.export as any).averageCost || 'Cost',
                    perUnit: (t.export as any).perUnit || '/',
                    pageLabel: (t.export as any).pageLabel || 'Page',
                },
            });
        } finally {
            setGenerating(false);
        }
    };

    const handleGenerateCSV = () => {
        const selectedExps = visibleEntries.filter((e: LogEntry) => enabledExpenses.has(e.id));
        
        const headers = [
            'Date',
            'Title',
            'Type',
            'Amount',
            'Currency',
            'Mileage',
            'Distance Unit',
            'Fuel (L/kWh)',
            'Description',
            'Parts'
        ];

        const rows = selectedExps.map(e => {
            const expenses = e.expenses || [];
            const totalAmount = expenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
            const types = Array.from(new Set(expenses.map(exp => getTypeName(exp.type)))).join(' / ');
            const fuel = expenses.reduce((sum, exp) => sum + (exp.fuel_liters || 0) + (exp.electricity_kwh || 0), 0);
            const desc = expenses.map(exp => exp.description).filter(Boolean).join('; ');
            const parts = (partsByExpense[e.id] || [])
                .filter(p => enabledParts.has(p.joinId))
                .map(p => `${p.part.name}${p.part.part_code ? ` (${p.part.part_code})` : ''} x${p.part.quantity}`)
                .join('; ');

            return [
                e.date,
                e.title || types || 'Expense',
                types,
                totalAmount.toFixed(2),
                effectivePreferences?.currency || 'EUR',
                e.mileage || '',
                effectivePreferences?.distance_unit || 'km',
                fuel || '',
                desc,
                parts
            ].map(val => `"${String(val || '').replace(/"/g, '""')}"`).join(',');
        });

        const csvContent = [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `MyCarCosts_Export_${selectedCar?.name || 'Car'}_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const slug = selectedCar?.slug || '';

    if (!isAuthorized) {
        return (
            <div className="flex flex-col items-center justify-center p-12 text-center h-[60vh]">
               <div className="w-16 h-16 bg-gray-100 dark:bg-white/5 rounded-full flex items-center justify-center mb-6 text-gray-400">
                  <FileDown size={32} />
               </div>
               <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{(t.common as any).accessDenied || 'Piekļuve liegta'}</h3>
               <p className="text-sm text-gray-500 dark:text-gray-400 max-w-[240px]">{(t.common as any).noExportPermission || 'Jums nav tiesību eksportēt šī auto datus.'}</p>
               <button onClick={onClose} className="mt-8 px-8 py-3 bg-blue-600 text-white rounded-2xl font-bold text-sm shadow-lg shadow-blue-500/20 active:scale-95 transition-all">
                    {(t.common as any).close || 'Aizvērt'}
               </button>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-transparent">
            <div className="flex-shrink-0">
                <NavigationBar
                    title={(t.export as any).title || 'Eksportēt datus'}
                    onBack={onClose}
                    fixed={false}
                    rightContent={
                        <button
                            type="button"
                            onClick={() => setShowFormatSelector(true)}
                            disabled={generating || enabledExpenses.size === 0}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white rounded-[10px] font-bold text-[13px] transition-all disabled:opacity-50 disabled:pointer-events-none"
                        >
                            {generating ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : (
                                <FileDown size={16} />
                            )}
                            <span className="hidden sm:inline">{(t.export as any).exportButton || 'Export'}</span>
                            <span className="sm:hidden">{(t.export as any).exportButton || 'Export'}</span>
                            {enabledExpenses.size > 0 && (
                                <span className="bg-white/20 px-1.5 py-0.5 rounded-md text-[11px] font-black leading-none ml-0.5">
                                    {enabledExpenses.size}
                                </span>
                            )}
                        </button>
                    }
                />
            </div>

            <div className="flex-1 overflow-y-auto no-scrollbar pb-12">
                <div className="max-w-2xl mx-auto px-3 pt-4 space-y-5">

                    {/* ── DATE RANGE ──────────────────────────────────────────────────── */}
                    <div className="rounded-2xl border border-gray-100 dark:border-white/10 bg-white dark:bg-slate-800 shadow-sm p-4">
                        <p className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-0.5">
                            {(t.export as any).period || 'Attēlojamais periods'}
                        </p>
                        <p className="text-xs text-gray-400 mb-3">
                            {(t.export as any).periodHint || 'Ja nenorādi periodu — tiek attēloti visi izdevumi'}
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                            <DateInput
                                label={(t.export as any).dateFrom}
                                value={dateFrom}
                                onChange={setDateFrom}
                                language={language}
                                placeholder={(t.export as any).dateFrom || 'No'}
                            />
                            <DateInput
                                label={(t.export as any).dateTo}
                                value={dateTo}
                                onChange={setDateTo}
                                language={language}
                                placeholder={(t.export as any).dateTo || 'Līdz'}
                            />
                        </div>
                        {(dateFrom || dateTo) && (
                            <button
                                type="button"
                                onClick={() => { setDateFrom(''); setDateTo(''); }}
                                className="mt-2 text-xs text-blue-500 font-semibold"
                            >
                                {(t.export as any).allTime || 'Notīrīt filtru'}
                            </button>
                        )}
                    </div>

                    {/* ── EXPENSE TYPES & ATTACHMENT TOGGLES ────────────────────────── */}
                    <div className="rounded-2xl border border-gray-100 dark:border-white/10 bg-white dark:bg-slate-800 shadow-sm p-4">
                        <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-3">
                            {(t.export as any).expenseTypes || 'Izdevumu veidi'}
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {allTypeKeys.map(key => {
                                const info = getTypeInfo(key);
                                const active = activeTypes.has(key);
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => {
                                            setActiveTypes(prev => {
                                                const next = new Set(prev);
                                                if (next.has(key)) next.delete(key);
                                                else next.add(key);
                                                return next;
                                            });
                                        }}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${active
                                            ? 'border-transparent text-white shadow-sm'
                                            : 'border-gray-200 dark:border-white/10 text-gray-400 dark:text-gray-500 hover:border-gray-300 dark:hover:border-white/20'}`}
                                        style={{ backgroundColor: active ? info.color : 'transparent' }}
                                    >
                                        <DynamicIcon name={info.icon} size={14} />
                                        {getTypeName(key)}
                                    </button>
                                );
                            })}
                        </div>

                        {(hasAnyFiles || hasAnyParts) && (
                            <>
                                <div className="h-px bg-gray-100 dark:bg-white/10 my-4" />
                                <div className="space-y-3">
                                    <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                                        {(t.export as any).attachmentsSection || 'Pielikumi PDF'}
                                    </p>
                                    {hasAnyFiles && (
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <ImageIcon size={16} className="text-blue-400" />
                                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    {(t.export as any).includeImages || 'Iekļaut failus'}
                                                </span>
                                            </div>
                                            <Toggle checked={globalIncludeFiles} onChange={handleGlobalFilesToggle} />
                                        </div>
                                    )}
                                    {hasAnyParts && (
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Wrench size={16} className="text-orange-400" />
                                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    {(t.export as any).includeParts || 'Iekļaut rezerves daļas'}
                                                </span>
                                            </div>
                                            <Toggle checked={globalIncludeParts} onChange={handleGlobalPartsToggle} />
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    {/* ── EXPENSES BY YEAR / MONTH ─────────────────────────────────────── */}
                    {visibleEntries.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-10 italic">
                            {(t.export as any).noExpenses || 'Nav izdevumu, kas atbilst filtriem'}
                        </p>
                    ) : (
                        <div className="space-y-10">
                            {grouped.map(yearGroup => (
                                <div key={yearGroup.year} className="space-y-6 pt-6 first:pt-0">
                                    {/* Year header */}
                                    <h2 className={`${theme.typography.sectionTitle} px-2`}>{yearGroup.year}</h2>

                                    {yearGroup.months.map(monthGroup => {
                                        const monthTotal = monthGroup.expenses.reduce((s, e) => {
                                            const entryTotal = (e.expenses || [])
                                                .filter(exp => exp.type !== 'info')
                                                .reduce((sum, exp) => sum + (exp.amount || 0), 0);
                                            return s + entryTotal;
                                        }, 0);
                                        return (
                                            <div key={monthGroup.key} className="space-y-2">
                                                {/* Month header */}
                                                <div className="flex items-center justify-between px-1">
                                                    <h3 className={`${theme.typography.sectionLabel} capitalize`}>{monthGroup.label} {yearGroup.year}</h3>
                                                    <span className="text-[13px] font-medium text-gray-400 dark:text-gray-300 font-inter">
                                                        {fmt(monthTotal)}
                                                    </span>
                                                </div>

                                                <div className="rounded-2xl border border-gray-100 dark:border-white/10 bg-white dark:bg-slate-800 shadow-sm overflow-hidden divide-y divide-gray-100 dark:divide-white/5">
                                                    {monthGroup.expenses.map((e) => {
                                                        const isOn = enabledExpenses.has(e.id);
                                                        const imgs = imagesByExpense[e.id] || [];
                                                        const parts = partsByExpense[e.id] || [];
                                                        const hasImgs = imgs.length > 0;
                                                        const hasParts = parts.length > 0;

                                                        return (
                                                            <div key={e.id}>
                                                                <div className={`flex items-center gap-2 pr-4 ${!isOn ? 'opacity-45' : ''} transition-opacity`}>
                                                                    <div className="flex-1 min-w-0">
                                                                        <LogEntryItem
                                                                            entry={e}
                                                                            slug={slug}
                                                                            effectivePreferences={effectivePreferences}
                                                                            openInNewTab={true}
                                                                        />
                                                                    </div>
                                                                    <Toggle
                                                                        checked={isOn}
                                                                        onChange={v => {
                                                                            setEnabledExpenses(prev => {
                                                                                const next = new Set(prev);
                                                                                if (v) next.add(e.id);
                                                                                else next.delete(e.id);
                                                                                return next;
                                                                            });
                                                                        }}
                                                                    />
                                                                </div>

                                                                {/* ─ Inline Images ─ */}
                                                                {isOn && hasImgs && (
                                                                    <div className="bg-gray-50/50 dark:bg-white/5 divide-y divide-gray-100 dark:divide-white/5 border-t border-gray-100 dark:border-white/5">
                                                                        {imgs.map(img => {
                                                                            const isImgOn = enabledImages.has(img.id);
                                                                            const isPdf = isPdfFile(img.image_url);
                                                                            const title = img.comment || img.image_url.split('/').pop() || 'file';

                                                                            return (
                                                                                <div
                                                                                    key={img.id}
                                                                                    className={`flex items-center gap-2 pl-12 pr-4 py-1 ${!isImgOn ? 'opacity-45' : ''} transition-opacity`}
                                                                                >
                                                                                    <div className="flex-1 min-w-0">
                                                                                        <GalleryItem
                                                                                            item={{
                                                                                                id: img.id,
                                                                                                imageUrl: img.image_url,
                                                                                                thumbnail: img.image_url_thumb || (isPdf ? undefined : img.image_url),
                                                                                                title,
                                                                                                subtitle: isPdf ? 'PDF' : 'Attēls',
                                                                                                onClick: () => {
                                                                                                    if (isPdf) {
                                                                                                        setPdfViewerUrl(img.image_url);
                                                                                                    } else {
                                                                                                        const allImgsForExpense = imgs.filter(i => !isPdfFile(i.image_url)).map(i => i.image_url);
                                                                                                        const idx = allImgsForExpense.indexOf(img.image_url);
                                                                                                        setViewerImages(allImgsForExpense);
                                                                                                        setViewerIndex(idx >= 0 ? idx : 0);
                                                                                                    }
                                                                                                },
                                                                                            }}
                                                                                            compact={true}
                                                                                        />
                                                                                    </div>
                                                                                    <Toggle
                                                                                        checked={isImgOn}
                                                                                        onChange={v => {
                                                                                            setEnabledImages(prev => {
                                                                                                const next = new Set(prev);
                                                                                                if (v) next.add(img.id);
                                                                                                else next.delete(img.id);
                                                                                                return next;
                                                                                            });
                                                                                        }}
                                                                                    />
                                                                                </div>
                                                                            );
                                                                        })}
                                                                        {/* Spacer/Separator after images block if parts follow or at least to visually end the block */}
                                                                        {!hasParts && <div className="h-2" />}
                                                                    </div>
                                                                )}

                                                                {/* ─ Inline Spare Parts ─ */}
                                                                {isOn && hasParts && (
                                                                    <div className={`bg-gray-50/50 dark:bg-white/5 divide-y divide-gray-100 dark:divide-white/5 border-t border-gray-100 dark:border-white/5 ${!hasImgs ? 'pt-0' : ''}`}>
                                                                        {parts.map(p => {
                                                                            const isPartOn = enabledParts.has(p.joinId);

                                                                            return (
                                                                                <div
                                                                                    key={p.joinId}
                                                                                    className={`flex items-center gap-2 pl-12 pr-4 py-1 ${!isPartOn ? 'opacity-45' : ''} transition-opacity`}
                                                                                >
                                                                                    <div className="flex-1 min-w-0">
                                                                                        <SparePartItem
                                                                                            part={p.part}
                                                                                            slug={slug}
                                                                                            compact={true}
                                                                                            onClick={ev => {
                                                                                                ev.stopPropagation();
                                                                                                window.open(`/${slug}/parts/${p.part.id}`, '_blank');
                                                                                            }}
                                                                                        />
                                                                                    </div>
                                                                                    <Toggle
                                                                                        checked={isPartOn}
                                                                                        onChange={v => {
                                                                                            setEnabledParts(prev => {
                                                                                                const next = new Set(prev);
                                                                                                if (v) next.add(p.joinId);
                                                                                                else next.delete(p.joinId);
                                                                                                return next;
                                                                                            });
                                                                                        }}
                                                                                    />
                                                                                </div>
                                                                            );
                                                                        })}
                                                                        <div className="h-2" />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* ── SUMMARY ─────────────────────────────────────────────────────── */}
                    {enabledExpenses.size > 0 && (
                        <div className="rounded-2xl border border-blue-100 dark:border-blue-500/20 bg-blue-50/60 dark:bg-blue-900/20 p-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <p className="text-xs text-blue-500 font-bold uppercase tracking-widest">{(t.export as any).totalExpenses || 'Kopā'}</p>
                                    <p className="text-xl font-black text-gray-900 dark:text-white mt-0.5">{fmt(selectedTotal)}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-blue-500 font-bold uppercase tracking-widest">{(t.export as any).totalItems || 'Ieraksti'}</p>
                                    <p className="text-xl font-black text-gray-900 dark:text-white mt-0.5">{enabledExpenses.size}</p>
                                </div>
                            </div>
                            {(enabledImages.size > 0 || enabledParts.size > 0) && (
                                <p className="text-xs text-blue-400 font-medium mt-2">
                                    + {enabledImages.size + enabledParts.size} {(t.export as any).attachmentsSection?.toLowerCase() || 'pielikumi'}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Export Format Selector Modal */}
            {showFormatSelector && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div 
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
                        onClick={() => setShowFormatSelector(false)}
                    />
                    <div className="relative bg-white dark:bg-[#00172D] rounded-[2.5rem] shadow-2xl w-full max-w-sm p-8 transform transition-all scale-100 animate-in fade-in zoom-in-95 duration-200 border border-transparent dark:border-white/5">
                        <div className="text-center mb-8 mt-2">
                            <h3 className="text-2xl font-black text-gray-900 dark:text-white mb-3">
                                {(t.export as any).formatSelectorTitle || 'Choose Format'}
                            </h3>
                            <p className="text-gray-500 dark:text-gray-400 font-medium leading-relaxed">
                                {(t.export as any).formatSelectorMsg || 'Please choose the export format'}
                            </p>
                        </div>

                        <div className="flex flex-col gap-3">
                            <button
                                onClick={() => {
                                    setShowFormatSelector(false);
                                    handleGeneratePDF();
                                }}
                                className="w-full py-4 px-4 rounded-2xl font-bold text-white shadow-lg transition-all active:scale-95 bg-blue-600 hover:bg-blue-700 dark:bg-blue-400 shadow-blue-500/20 dark:shadow-none flex items-center justify-center gap-2"
                            >
                                <FileDown size={20} />
                                {(t.export as any).generatePDF || 'Generate PDF'}
                            </button>
                            <button
                                onClick={() => {
                                    setShowFormatSelector(false);
                                    handleGenerateCSV();
                                }}
                                className="w-full py-4 px-4 rounded-2xl font-bold text-gray-800 dark:text-white bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/15 transition-all active:scale-95 flex items-center justify-center gap-2 border border-gray-200 dark:border-white/5"
                            >
                                <FileDown size={20} className="text-green-500" />
                                {(t.export as any).generateCSV || 'Generate CSV'}
                            </button>
                            <button
                                onClick={() => setShowFormatSelector(false)}
                                className="w-full py-4 px-4 rounded-2xl font-bold text-gray-500 hover:bg-gray-50 dark:hover:bg-white/5 transition-all"
                            >
                                {t.common.cancel}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── VIEWERS ────────────────────────────────────────────────────────────── */}
            {viewerImages && (
                <ImageViewer
                    images={viewerImages}
                    initialIndex={viewerIndex}
                    onClose={() => setViewerImages(null)}
                />
            )}
            {pdfViewerUrl && (
                <PdfViewer
                    url={pdfViewerUrl}
                    onClose={() => setPdfViewerUrl(null)}
                />
            )}
            <AlertModal
                isOpen={isUpgradeModalOpen}
                onClose={() => setIsUpgradeModalOpen(false)}
                title={(t.export as any).exportButton || 'Datu eksports'}
                message={(t.export as any).exportRestrictedPopup || 'Datu eksports ir pieejams tikai Pro lietotājiem.'}
                confirmLabel={(t.common as any).ok || 'OK'}
                secondaryAction={{
                    label: t.settings.upgradePlan || 'Uzlabot plānu',
                    onClick: () => navigate('/settings/plans')
                }}
            />
        </div>
    );
}
